import * as amplitude from '@amplitude/analytics-browser';
import { EnrichmentPlugin, Event } from '@amplitude/analytics-types';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { AmplitudeAutoTrackEvents } from '@/components/Analytics/amplitude.types';
import { AmplitudeCatalogStatus } from '@/components/Analytics/Plugins/plugins.type';
import { getAmplitudeCatalogStatus } from '@/components/Analytics/Plugins/plugins.utils';
import { useAppSelector } from '@/redux/hooks';
import { getCatalogStatus } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { AmplitudePageName, getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';
import { getIdFromPath } from '@/utils/urls';

/**
 * A plugin that adds catalog-related fields to the auto-tracked page view events of the catalog page
 */
class CatalogPagePropertiesPlugin implements EnrichmentPlugin {
    name = 'catalog-page-properties-plugin';
    type = 'enrichment' as const;

    private catalogStatus = AmplitudeCatalogStatus.None;

    /**
     * Provide the catalog information from the page, so that when the event is run, the data is available
     * @param catalogStatus - What is the status of the catalog?
     */
    addCatalogInfo = (catalogStatus: typeof this.catalogStatus) => {
        this.catalogStatus = catalogStatus;
    };

    /**
     * <em>This function should not be called directly.</em>
     *
     * This is called by amplitude itself when any event is triggered and the plugin is registered
     *
     * @param event - The amplitude event that was triggered. Could be an auto-track event or one of our own defined events.
     * In the case of this plugin, we filter out anything that is not an auto-tracked page view of the catalog page.
     */
    execute = async (event: Event) => {
        const autoTrackUrl = event.event_properties?.['[Amplitude] Page URL'];
        if (autoTrackUrl) {
            const pageName = getAmplitudePageNameFromPath(autoTrackUrl);
            if (event.event_type === AmplitudeAutoTrackEvents.PageViewed && pageName === AmplitudePageName.Catalog) {
                event.event_properties['Catalog Status'] = this.catalogStatus;
            }
        }
        return event;
    };
}

/**
 * <em>This should only be used inside the AmplitudeProvider.</em>
 *
 * A hook that handles the initialization, usage, and cleanup of the CatalogPagePropertiesPlugin.
 */
export const useCatalogPagePropertiesPlugin = () => {
    const { pathname } = useLocation();
    const [currentCatalogPagePlugin, setCurrentCatalogPagePlugin] = useState('');
    const catalogId = getIdFromPath(pathname);
    const catalogStatus = useAppSelector((state) => getCatalogStatus(state, catalogId));

    useEffect(() => {
        if (currentCatalogPagePlugin) {
            amplitude.remove(currentCatalogPagePlugin);
        }

        // we only add a new instance of the plugin to amplitude on the catalog page
        const pageName = getAmplitudePageNameFromPath(pathname);
        const amplitudeCatalogStatus = getAmplitudeCatalogStatus(catalogStatus);
        if (pageName === AmplitudePageName.Catalog && Boolean(amplitudeCatalogStatus)) {
            // instantiate the plugin; this is not yet added to amplitude, so we can modify it further
            const plugin = new CatalogPagePropertiesPlugin();
            // pass in the required information to our plugin. In this case, the catalog status
            plugin.addCatalogInfo(amplitudeCatalogStatus);
            // add the completed plugin to amplitude
            amplitude.add(plugin);
            // store the name for when we need to remove it
            setCurrentCatalogPagePlugin(plugin.name);
        }
    }, [catalogStatus, currentCatalogPagePlugin, pathname]);
};
